import * as React from 'react';
import {useEffect, useRef, useState} from 'react';
import {createEmbeddingContext} from 'amazon-quicksight-embedding-sdk';
import {LoadingOverlay} from '@finpay-development/shared-components';
import {Alert, Box} from '@mui/material';

interface QuickSightEmbedProps {
  dashboardUrl: string | null;
  isLoading?: boolean;
  height?: string;
}

function QuickSightEmbed({
  dashboardUrl,
  isLoading = false,
  height = '680px',
}: QuickSightEmbedProps) {
  const dashboardRef = useRef<HTMLDivElement | null>(null);
  const [embeddingContext, setEmbeddingContext] = useState<any | null>(null);

  useEffect(() => {
    if (!dashboardUrl) {
      setEmbeddingContext(null);
      return;
    }

    let cancelled = false;

    createEmbeddingContext().then((context) => {
      if (!cancelled) {
        setEmbeddingContext(context);
      }
    }).catch((error) => {
      console.error('Error creating QuickSight embedding context:', error);
    });

    return () => {
      cancelled = true;
    };
  }, [dashboardUrl]);

  useEffect(() => {
    if (!embeddingContext || !dashboardUrl || !dashboardRef.current) {
      return;
    }

    const options = {
      url: dashboardUrl,
      container: dashboardRef.current,
      height,
      width: '100%',
    };

    embeddingContext.embedDashboard(options).catch((error: unknown) => {
      console.error('Error embedding dashboard:', error);
    });
  }, [dashboardUrl, embeddingContext, height]);

  if (isLoading) {
    return <LoadingOverlay />;
  }

  if (!dashboardUrl) {
    return (
      <Box sx={{maxWidth: 640, margin: '48px auto', padding: 2}}>
        <Alert severity="error">Unable to load this report.</Alert>
      </Box>
    );
  }

  return (
    <main style={{display: 'flex', flexDirection: 'column'}}>
      <div ref={dashboardRef} style={{overflow: 'hidden'}} />
    </main>
  );
}

export default QuickSightEmbed;
